// fetch() que aguenta a API sumir por alguns segundos — pedido 2026-10-01
// ("failed to fetch ... isso nunca pode acontecer"). Causa real achada nos
// logs do Nginx: a cada deploy o container do backend (e, mais raro, o
// Traccar) reinicia e fica ~5-10s sem aceitar conexão; o Nginx devolve 502
// SEM cabeçalho CORS (inclusive no preflight OPTIONS), e o navegador só
// mostra "TypeError: Failed to fetch". Quem estava salvando um usuário
// naquele instante via o erro na cara (ex.: 30/09 19:14, edição do usuário
// 1314).
//
// Só repete quando o fetch nem chegou a ter resposta (TypeError de rede) —
// nesse caso a requisição não foi processada (conexão recusada/preflight
// falhou), então repetir é seguro mesmo para POST/PUT. Qualquer resposta
// HTTP de verdade (4xx/5xx legíveis) volta na hora, sem repetir.
// Cancelamento (AbortController) nunca é repetido.
//
// 429 (limite de requisições do backend, 2026-10-01) também é repetido: o
// backend recusa ANTES de processar, então repetir um PUT/POST é seguro —
// antes o "Salvar" de veículo/usuário levava 429 e simplesmente não gravava.
//
// 2026-10-26 ("deixo o navegador aberto 10 min, mapa cinza e não salva"):
// os logs do Nginx mostraram que só UM computador do escritório (Windows)
// ficou ~2 min sem mandar nada pro servidor enquanto os outros da mesma
// rede seguiam normais — a rede daquele PC some depois de ocioso. 15 s de
// tentativas não bastavam; agora espera até ~1 min antes de desistir.
//
// 2026-10-27 (mesmo PC, 4,5 min sem rede no meio do "Salvar alterações" de
// um veículo: "nunca pode cair o sistema"): GRAVAÇÃO (PUT/POST/PATCH/
// DELETE) não desiste mais — continua tentando a cada 10 s até a conexão
// voltar e grava sozinha; a tela mostra o aviso de ConnectionLostBanner
// enquanto isso. Leitura (GET) continua desistindo em ~1 min: o polling
// seguinte já pede de novo.
const RETRY_DELAYS_MS = [1000, 2000, 3000, 4000, 5000, 5000, 10_000, 10_000, 10_000, 10_000];
const WRITE_RETRY_DELAY_MS = 10_000;
const RATE_LIMIT_RETRIES = 3;

// Disparado quando uma chamada falha por rede (uma vez por queda) — o
// ConnectionLostBanner mostra o aviso até NETWORK_RESTORED_EVENT.
export const NETWORK_LOST_EVENT = "gd:network-lost";

// Disparado uma vez quando uma chamada volta a ter resposta depois de uma
// falha de rede — o evento "online" do navegador não dispara nesse caso
// (a placa continua "conectada"). Mapa e sessão escutam pra se refazer na
// hora (map-tile-recovery.tsx, gpsdrive-client.ts).
export const NETWORK_RESTORED_EVENT = "gd:network-restored";
let networkFailing = false;

function markNetworkOk(): void {
  if (!networkFailing) return;
  networkFailing = false;
  if (typeof window !== "undefined") window.dispatchEvent(new Event(NETWORK_RESTORED_EVENT));
}

export async function fetchWithNetworkRetry(
  input: string,
  init: RequestInit,
): Promise<Response> {
  let rateLimitAttempt = 0;
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await fetch(input, init);
      markNetworkOk();
      if (res.status === 429 && rateLimitAttempt < RATE_LIMIT_RETRIES && !init.signal?.aborted) {
        rateLimitAttempt++;
        const retryAfterSec = Number(res.headers.get("retry-after"));
        const waitMs =
          Number.isFinite(retryAfterSec) && retryAfterSec > 0
            ? Math.min(retryAfterSec * 1000, 10_000)
            : rateLimitAttempt * 2000;
        await new Promise((r) => setTimeout(r, waitMs));
        attempt--;
        continue;
      }
      return res;
    } catch (err) {
      const aborted =
        init.signal?.aborted || (err instanceof DOMException && err.name === "AbortError");
      if (aborted || !(err instanceof TypeError)) throw err;
      if (!networkFailing) {
        networkFailing = true;
        if (typeof window !== "undefined") window.dispatchEvent(new Event(NETWORK_LOST_EVENT));
      }
      const method = (init.method ?? "GET").toUpperCase();
      if (method !== "GET" && method !== "HEAD") {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt] ?? WRITE_RETRY_DELAY_MS));
        continue;
      }
      if (attempt >= RETRY_DELAYS_MS.length) {
        // 2026-10-02: o operador via "Não foi salvo: Failed to fetch" (texto
        // cru do navegador). Continua TypeError pra quem checa o tipo.
        throw new TypeError(
          "Sem conexão com o servidor há 1 minuto. Verifique a internet deste computador e tente de novo.",
        );
      }
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
    }
  }
}
